import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface NavItem {
  label: string;
  href: string;
  children?: { label: string; href: string }[];
}

const navItems: NavItem[] = [
  { label: 'ABOUT', href: '/about' },
  {
    label: 'PRODUCTS',
    href: '#',
    children: [
      { label: 'SWORDFISH', href: '/swordfish' },
      { label: 'TUNA', href: '/tuna' },
      { label: 'SALMON', href: '/salmon' },
      { label: 'SCALLOPS', href: '/scallops' },
      { label: 'LOBSTER', href: '/lobster' },
    ],
  },
  { label: 'CONTACT', href: '/contact' },
];

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  return (
    <header
      className={`w-full transition-all duration-300 ${
        scrolled
          ? 'fixed top-0 left-0 right-0 z-50 bg-primary-900 shadow-lg'
          : 'relative z-50 bg-primary-900'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Left: Logo + Cert */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img
                              src="/images/logo.png"
                alt="Finest Kind Seafood LLC"
                className="h-18 md:h-20 w-auto object-contain"
              />
            </Link>
            
          </div>

          {/* Center: Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={item.href}
                  className="flex items-center gap-1 px-2 py-2 text-sm font-medium tracking-[0.15em] text-white hover:text-primary-300 transition-colors whitespace-nowrap"
                >
                  {item.label}
                  {item.children && (
                    <i className="ri-arrow-down-s-line text-xs" />
                  )}
                </Link>
                {item.children && activeDropdown === item.label && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-0 bg-primary-800 border border-primary-700 min-w-[200px] shadow-xl">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.href}
                        className="block px-4 py-2.5 text-xs text-white hover:bg-primary-700 hover:text-primary-300 transition-colors whitespace-nowrap text-center"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right: Phone */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <i className="ri-phone-line text-primary-300 text-sm" />
            <a
              href=""
              className="text-sm text-white/80 hover:text-white transition-colors whitespace-nowrap tracking-wider"
            >
              (380) 213-8122
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2 flex-shrink-0"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-primary-800 border-t border-primary-700">
          <nav className="px-4 py-4 space-y-1">
            {navItems.map((item) => (
              <div key={item.label}>
                <Link
                  to={item.href}
                  className="block py-2 text-sm text-white hover:text-primary-300 transition-colors"
                  onClick={() => !item.children && setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
                {item.children && (
                  <div className="pl-4 space-y-1">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.href}
                        className="block py-1.5 text-xs text-primary-300 hover:text-white transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-3 mt-3 border-t border-primary-700">
              <a
                href=""
                className="flex items-center gap-2 py-2 text-sm text-primary-300 hover:text-white transition-colors"
              >
                <i className="ri-phone-line" />
                (380) 213-8122
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}